import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import ButtonBack from "../utils/ButtonBack";
import { useOrganization } from "../utils/OrganizationContext";
import ThemedSelect from "../utils/ThemedSelect";
import ErrorModal from "../utils/ErrorModal";
import ThemedDatePicker from "../utils/ThemedDatePicker";
import ThemedTimePicker from "../utils/ThemedTimePicker";

/* ---------- Direction C tokens (styling only) — same as Add lead ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

const SEC_COLORS = { who: "#12A08F", related: "#E0911B", reminder: "#6D5DE8" };

const formTheme = createTheme({
  palette: {
    primary: { main: PRIMARY, dark: PRIMARY_DARK, contrastText: "#FFFFFF" },
    error: { main: ERROR },
    text: { primary: INK, secondary: "#4A5A6E" },
  },
  shape: { borderRadius: 8 },
  typography: { fontFamily: FONT, button: { textTransform: "none", fontWeight: 600 } },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          background: FIELD_BG,
          fontFamily: FONT,
          fontSize: 13.5,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
          "&.Mui-focused": { background: "#FFFFFF" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ERROR },
        },
        input: { "&::placeholder": { color: "#8A97A8", opacity: 1 } },
      },
    },
    MuiFormHelperText: { styleOverrides: { root: { display: "none" } } },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { marginTop: 4, borderRadius: 12, border: `1px solid ${LINE}`, boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)" },
        option: { fontFamily: FONT, fontSize: 13.5, borderRadius: 8, marginInline: 6 },
      },
    },
  },
});

const card = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, marginBottom: 14, scrollMarginTop: 90 };
const cardHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${LINE}` };
const cardTitle = { display: "flex", alignItems: "center", gap: 9, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: INK_2 };
const dot = (c, size = 6) => ({ width: size, height: size, flex: `0 0 ${size}px`, borderRadius: "50%", background: c });
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const lbl = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const errText = { display: "block", margin: "4px 0 0", fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: ERROR };
const acSx = {
  margin: 0,
  "& .MuiInputBase-root": {
    minHeight: 36,
    fontSize: "13.5px",
    borderRadius: "8px",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "8px !important",
  },
  "& .MuiInputBase-input": { padding: "7px 4px !important", minWidth: "0 !important" },
};
const primaryBtn = {
  height: 38, px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: `${PRIMARY} !important`, color: "#FFFFFF !important", border: "0 !important",
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none", whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` }, "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: 38, px: 2, borderRadius: "10px", background: "#FFFFFF", color: INK, border: `1px solid ${FIELD_EDGE}`,
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", "&:hover": { background: "#F4F7FA" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: `repeat(${n}, minmax(0,1fr))` },
});

// Scoped resets so global App.css rules can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-rem .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-rem .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-rem .dc-card textarea{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;font-size:13.5px!important;font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;}
.dc-rem .dc-card textarea::placeholder{color:#8A97A8;opacity:1;}
.dc-rem .dc-card .MuiAutocomplete-root,.dc-rem .dc-card .MuiInputBase-root{width:100%!important;max-width:none!important;}
`;

const FOR_OPTIONS = [
  { value: "SELF", label: "Self" },
  { value: "TEAM", label: "Team" },
  { value: "USER", label: "Particular User" },
];
const CONTEXT_OPTIONS = [
  { value: "", label: "General" },
  { value: "LEAD", label: "Lead" },
  { value: "INVENTORY", label: "Inventory" },
];

const EditReminder = () => {
  const { reminderId } = useParams();
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const orgId = JSON.parse(localStorage.getItem("org"));

  const [loading, setLoading] = useState(false);

  const [forType, setForType] = useState(""); // SELF | TEAM | USER
  const [contextType, setContextType] = useState(""); // GENERAL | LEAD | INVENTORY

  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [inventories, setInventories] = useState([]);

  const [form, setForm] = useState({
    orgUserId: "",
    teamId: "",
    leadId: "",
    inventoryId: "",
    text: "",
    dateTime: "",
    projectId: "",
  });
  const [errors, setErrors] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const afterClose = useRef(null);

  const showModal = (msg, type, onClose = null) => {
    setModalMsg(msg);
    setModalType(type);
    afterClose.current = onClose;
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    const next = afterClose.current;
    afterClose.current = null;
    if (next) next();
  };
  // const [role, setRole] = useState();
  const { role } = useOrganization();

  // useEffect(() => {
  //   if (!storedUser?.accessToken) return;
  //   const decoded = jwtDecode(storedUser.accessToken);
  //   const role = decoded.userRole[0];
  //   setRole(role);
  // }, []);

  useEffect(() => {
    const fetchReminder = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/reminder/by-id?reminderId=${reminderId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );

        const r = res.data;

        setForType(r.reminderFor || "");
        setContextType(r?.lead ? "LEAD" : r?.inventory ? "INVENTORY" : "");

        setForm({
          orgUserId: r.orgUser?.orgUserId || "",
          teamId: r.team?.teamId || "",
          leadId: r.lead?.leadId || "",
          inventoryId: r.inventory?.inventoryId || "",
          projectId:
            r.lead?.project?.projectId || r.inventory?.project?.projectId || "",
          text: r.text || "",
          dateTime: r.dateTime?.slice(0, 16) || "",
        });
      } catch (err) {
        showModal("Failed to load reminder", "error", () => navigate(-1));
      }
    };

    fetchReminder();
  }, [reminderId]);

  useEffect(() => {
    if (!role) return;
    if (role === "ADMIN") {
      fetchTeams();
      fetchUsersForAdmin();
    } else {
      fetchUsers();
      fetchTeamsForUser();
    }
  }, [role, orgId]);

  useEffect(() => {
    if (!form.projectId) return;
    fetchLeads();
    fetchInventories();
  }, [form.projectId]);

  const fetchUsers = async () => {
    try {
      const [usersRes, adminsRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/all?orgId=${orgId}&username=${storedUser.username}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/admins?orgId=${orgId}&username=${storedUser.username}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        ),
      ]);

      const users = (usersRes.data || []).map((user) => ({
        ...user,
        type: "USER",
      }));
      const admins = (adminsRes.data || []).map((admin) => ({
        ...admin,
        type: "ADMIN",
      }));

      setUsers([...users, ...admins]);
    } catch (error) {
      console.log("Error fetching users:", error);
      setUsers([]);
    }
  };

  const fetchUsersForAdmin = async () => {
    try {
      const [usersRes, adminsRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/all?orgId=${orgId}&username=${storedUser.username}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/admins?orgId=${orgId}&username=${storedUser.username}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        ),
      ]);

      const users = usersRes.data || [];
      const admins = adminsRes.data || [];

      const otherAdmins = admins.filter(
        (admin) => admin.mobile !== storedUser.username,
      );

      setUsers([...users, ...otherAdmins]);
    } catch (error) {
      console.log("Error fetching users/admins:", error);
      setUsers([]);
    }
  };

  const fetchTeams = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/team/all?orgId=${orgId}`,
      { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
    );
    setTeams(res.data || []);
    setProjects(
      (res.data?.flatMap((data) => data.projects || []) || []).filter(
        (project, index, self) =>
          index === self.findIndex((p) => p.projectId === project.projectId),
      ),
    );
  };

  const fetchTeamsForUser = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/team/user/all?orgId=${orgId}`,
      { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
    );
    setTeams(res.data || []);
    setProjects(
      (res.data?.flatMap((data) => data.projects || []) || []).filter(
        (project, index, self) =>
          index === self.findIndex((p) => p.projectId === project.projectId),
      ),
    );
  };

  const fetchLeads = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/lead/all?projectId=${form.projectId}&orgId=${orgId}`,
      { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
    );
    setLeads(res.data || []);
  };

  const fetchInventories = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/inventory/project?projectId=${form.projectId}`,
      { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
    );
    setInventories(res.data || []);
  };

  const getRequiredFields = () => {
    const fields = ["text", "dateTime", "forType"];

    if (forType === "TEAM") fields.push("teamId");
    if (forType === "USER") fields.push("orgUserId");

    if (contextType !== "") fields.push("projectId");
    if (contextType === "LEAD") fields.push("leadId");
    if (contextType === "INVENTORY") fields.push("inventoryId");

    return fields;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  // The datetime-local input is split into the themed date + time pickers.
  // They write the same "YYYY-MM-DDTHH:mm" string into form.dateTime via
  // handleChange, so validation and the payload are unchanged.
  const [dtDate, dtTimeFromForm] = (form.dateTime || "").split("T");
  const [dtTime, setDtTime] = useState("");
  const shownTime = dtTimeFromForm || dtTime;
  const onReminderDate = (e) =>
    handleChange({
      target: { name: "dateTime", value: e.target.value ? `${e.target.value}T${shownTime || "09:00"}` : "" },
    });
  const onReminderTime = (e) => {
    setDtTime(e.target.value);
    if (dtDate) {
      handleChange({ target: { name: "dateTime", value: `${dtDate}T${e.target.value}` } });
    }
  };

  const handleUpdate = async () => {
    const newErrors = {};
    const requiredFields = getRequiredFields();

    requiredFields.forEach((field) => {
      if (field === "forType") {
        if (!forType) {
          newErrors.forType = "This field is required";
        }
      } else {
        if (!form[field] || form[field].toString().trim() === "") {
          newErrors[field] = "This field is required";
        }
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      reminderId,
      orgId,
      text: form.text,
      dateTime: form.dateTime,
      reminderFor: forType,
      orgUserId: forType === "USER" ? form.orgUserId : null,
      teamId: forType === "TEAM" ? form.teamId : null,
      leadId: contextType === "LEAD" ? form.leadId : null,
      inventoryId: contextType === "INVENTORY" ? form.inventoryId : null,
    };

    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/reminder`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      localStorage.removeItem(`${`reminder_${reminderId}`}`);
      showModal("Reminder updated successfully", "success", () => navigate(-1));
    } catch (err) {
      showModal(err?.response?.data?.Exception || "Failed to update reminder", "error");
    } finally {
      setLoading(false);
    }
  };


  /* ---------- layout (markup only — same state, handlers and validation) ---------- */
  const isMobile = useMediaQuery("(max-width: 750px)");
  const hideSectionNav = useMediaQuery("(max-width: 1000px)");
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState("who");

  const fieldBox = (key, label, required, control, errKey, full) => (
    <Box key={key} sx={full ? { gridColumn: "1 / -1" } : undefined}>
      <label style={lbl}>
        {label}
        {required && <span style={req}>*</span>}
      </label>
      {control}
      {errors[errKey || key] && (
        <span className="error-text" style={errText}>{errors[errKey || key]}</span>
      )}
    </Box>
  );

  // Simple option pickers (Reminder For / Related To) — same values and setters
  const optionPicker = (name, options, value, onPick, placeholder, err) => (
    <Autocomplete
      name={name}
      disableClearable
      options={options}
      getOptionLabel={(o) => o.label}
      value={options.find((o) => o.value === value) || null}
      onChange={(e, o) => onPick({ target: { name, value: o?.value ?? "" } })}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} error={!!err} />
      )}
      sx={acSx}
    />
  );

  const whoItems = [
    fieldBox(
      "forType",
      "Reminder For",
      true,
      optionPicker("forType", FOR_OPTIONS, forType, (e) => setForType(e.target.value), "Select", errors.forType),
    ),
    forType === "TEAM" &&
      fieldBox(
        "teamId",
        "Select Team",
        true,
        <Autocomplete
          name="teamId"
          className={`${errors.teamId ? "input-error" : ""}`}
          options={teams || []}
          getOptionLabel={(option) => option.name || ""}
          value={teams?.find((t) => t.teamId === form.teamId) || null}
          onChange={(e, value) => {
            setForm((prev) => ({
              ...prev,
              teamId: value?.teamId || "",
            }));
          }}
          isOptionEqualToValue={(option, value) => option.teamId === value.teamId}
          renderInput={(params) => (
            <TextField {...params} name="teamId" placeholder="Select Team" error={!!errors.teamId} helperText={errors.teamId} />
          )}
          sx={acSx}
        />,
      ),
    forType === "USER" &&
      fieldBox(
        "orgUserId",
        "Select User",
        true,
        <Autocomplete
          name="orgUserId"
          className={`${errors.orgUserId ? "input-error" : ""}`}
          options={users || []}
          getOptionLabel={(option) => `${option.name || "Name"} (${option.userRoleName})`}
          value={
            users?.find(
              (u) => (u.type === "ADMIN" ? u.mobile : u.orgUserId) === form.orgUserId,
            ) || null
          }
          onChange={(e, value) => {
            setForm((prev) => ({
              ...prev,
              orgUserId: value?.type === "ADMIN" ? value.mobile : value?.orgUserId || "",
            }));
          }}
          isOptionEqualToValue={(option, value) =>
            option.type === value.type &&
            (option.type === "ADMIN"
              ? option.mobile === value.mobile
              : option.orgUserId === value.orgUserId)
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Select User" error={!!errors.orgUserId} helperText={errors.orgUserId} />
          )}
          sx={acSx}
        />,
      ),
  ].filter(Boolean);

  const relatedItems = [
    fieldBox(
      "contextType",
      "Reminder Related To",
      true,
      optionPicker("contextType", CONTEXT_OPTIONS, contextType, (e) => setContextType(e.target.value), "General", errors.contextType),
    ),
    contextType !== "" &&
      fieldBox(
        "projectId",
        "Select Project",
        true,
        <Autocomplete
          name="projectId"
          className={`${errors.projectId ? "input-error" : ""}`}
          options={projects || []}
          getOptionLabel={(option) => option.projectName || ""}
          value={projects?.find((p) => p.projectId === form.projectId) || null}
          onChange={(e, value) => {
            setForm((prev) => ({
              ...prev,
              projectId: value?.projectId || "",
            }));
          }}
          isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Project" error={!!errors.projectId} helperText={errors.projectId} />
          )}
          sx={acSx}
        />,
      ),
    contextType === "LEAD" &&
      fieldBox(
        "leadId",
        "Select Lead",
        true,
        <Autocomplete
          name="leadId"
          className={`${errors.leadId ? "input-error" : ""}`}
          options={leads || []}
          getOptionLabel={(option) => option.name || ""}
          value={leads?.find((l) => l.leadId === form.leadId) || null}
          onChange={(e, value) => {
            setForm((prev) => ({
              ...prev,
              leadId: value?.leadId || "",
            }));
          }}
          isOptionEqualToValue={(option, value) => option.leadId === value.leadId}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Lead" error={!!errors.leadId} helperText={errors.leadId} />
          )}
          sx={acSx}
        />,
      ),
    contextType === "INVENTORY" &&
      fieldBox(
        "inventoryId",
        "Select Inventory",
        true,
        <Autocomplete
          name="inventoryId"
          className={`${errors.inventoryId ? "input-error" : ""}`}
          options={inventories || []}
          getOptionLabel={(option) => option.type || ""}
          value={inventories?.find((i) => i.inventoryId === form.inventoryId) || null}
          onChange={(e, value) => {
            setForm((prev) => ({
              ...prev,
              inventoryId: value?.inventoryId || "",
            }));
          }}
          isOptionEqualToValue={(option, value) => option.inventoryId === value.inventoryId}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Inventory" error={!!errors.inventoryId} helperText={errors.inventoryId} />
          )}
          sx={acSx}
        />,
      ),
  ].filter(Boolean);

  const reminderItems = [
    fieldBox(
      "dateTime",
      "Date",
      true,
      <ThemedDatePicker
        name="dateTime"
        value={dtDate || ""}
        onChange={onReminderDate}
        error={!!errors.dateTime}
      />,
    ),
    fieldBox(
      "dateTimeTime",
      "Time",
      true,
      <ThemedTimePicker
        name="dateTimeTime"
        value={shownTime || ""}
        onChange={onReminderTime}
        error={!!errors.dateTime}
      />,
      "__none__",
    ),
    fieldBox(
      "text",
      "Reminder Text",
      true,
      <textarea
        className={`${errors.text ? "input-error" : ""}`}
        name="text"
        rows={3}
        value={form.text}
        onChange={handleChange}
        placeholder="What should this reminder say?"
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          minHeight: 72,
          padding: "9px 12px",
          margin: 0,
          background: FIELD_BG,
          border: `1px solid ${errors.text ? ERROR : FIELD_EDGE}`,
          borderRadius: 8,
          fontFamily: FONT,
          fontSize: 13.5,
          lineHeight: 1.5,
          color: INK,
          outline: "none",
          resize: "vertical",
        }}
      />,
      undefined,
      true,
    ),
  ];

  const reqIn = (keys) => keys.filter((k) => getRequiredFields().includes(k)).length;
  const sections = [
    { id: "who", title: "Who", items: whoItems, meta: `${reqIn(["forType", "teamId", "orgUserId"])} required` },
    {
      id: "related",
      title: "Related to",
      items: relatedItems,
      meta: contextType === "" ? "general" : `${1 + reqIn(["projectId", "leadId", "inventoryId"])} required`,
    },
    { id: "reminder", title: "Reminder", items: reminderItems, meta: "3 required" },
  ];

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActiveSection(en.target.dataset.sec);
        });
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderSection = (s) => (
    <section key={s.id} data-sec={s.id} ref={(el) => (sectionRefs.current[s.id] = el)} className="dc-card" style={card}>
      <div style={cardHead}>
        <span style={cardTitle}>
          <span style={dot(SEC_COLORS[s.id])} />
          {s.title}
        </span>
        <span style={cardMeta}>{s.meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        <Box sx={cols(4)}>{s.items}</Box>
      </Box>
    </section>
  );

  const sectionNav = (
    <nav style={{ position: "sticky", top: 16, background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, padding: 8 }}>
      {sections.map((s) => {
        const on = activeSection === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => scrollToSection(s.id)}
            style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%", height: 36, padding: "0 10px",
              marginBottom: 2, border: 0, borderRadius: 9, background: on ? "#EAF2FB" : "transparent",
              cursor: "pointer", fontFamily: FONT, fontSize: 13.5, fontWeight: on ? 700 : 500, color: INK, textAlign: "left",
            }}
          >
            <span style={dot(SEC_COLORS[s.id], 7)} />
            <span style={{ flex: 1 }}>{s.title}</span>
            <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>{s.items.length}</span>
          </button>
        );
      })}
    </nav>
  );

  const goBack = () => navigate("/dashboard/reminder");

  const saveButton = (
    <Button className="btn-contained" onClick={handleUpdate} disabled={loading} sx={primaryBtn}>
      {loading ? "Updating..." : "Update reminder"}
    </Button>
  );

  return (
    <ThemeProvider theme={formTheme}>
      <style>{SCOPED_CSS}</style>
      <div className="dc-rem" style={{ fontFamily: FONT, color: INK, textAlign: "left", padding: isMobile ? "4px 0 88px" : "4px 0 32px" }}>

        {/* ---------------- PAGE HEADER ---------------- */}
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
          <div style={{ minWidth: 0 }}>
            <button
              type="button"
              onClick={goBack}
              style={{ border: 0, background: "transparent", padding: 0, marginBottom: 14, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
            >
              ← Reminders
            </button>
            <h1 style={{ margin: 0, fontFamily: FONT, fontSize: isMobile ? 24 : 28, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: INK }}>
              Edit reminder
            </h1>
            <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2 }}>Update who it's for, what it's about and when</div>
          </div>
          {!isMobile && (
            <Box sx={{ display: "flex", gap: 1.25, flex: "0 0 auto" }}>
              <Button onClick={goBack} sx={ghostBtn}>Cancel</Button>
              {saveButton}
            </Box>
          )}
        </Box>

        {/* ---------------- NAV + SECTIONS ---------------- */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: hideSectionNav ? "minmax(0,1fr)" : "200px minmax(0,1fr)",
            gap: "14px",
            alignItems: "start",
          }}
        >
          {!hideSectionNav && sectionNav}
          <div>{sections.map(renderSection)}</div>
        </Box>

        {/* ---------------- MOBILE ACTION BAR ---------------- */}
        {isMobile && (
          <Box
            className="btn-container"
            sx={{
              position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 1.25,
              p: "10px 16px", background: "#FFFFFF", borderTop: `1px solid ${LINE}`, boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
            }}
          >
            <ButtonBack handleBack={goBack} />
            <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>{saveButton}</Box>
          </Box>
        )}

        <ErrorModal
          open={modalOpen}
          handleClose={handleModalClose}
          msg={modalMsg}
          type={modalType}
        />
      </div>
    </ThemeProvider>
  );
};

export default EditReminder;